import React from 'react';
import { Server, Wrench, FileText, Terminal } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface McpTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const McpTab: React.FC<McpTabProps> = ({ data, onChange }) => {
    const operation = data.mcpOperation || 'CallTool';

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-800 flex gap-3">
                <Server className="text-slate-600 dark:text-slate-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-300">MCP Client Configuration</p>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                        Connect to a Model Context Protocol server to invoke tools, read resources, or fetch prompts.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <PropertyField 
                    config={{ 
                        name: 'mcpServerUrl', 
                        label: 'Server URL', 
                        type: 'url', 
                        placeholder: 'http://localhost:3000/mcp',
                        validation: { required: true, url: true }
                    }}
                    value={data.mcpServerUrl}
                    onChange={onChange}
                />

                <PropertyField 
                    config={{ 
                        name: 'mcpOperation', 
                        label: 'Operation', 
                        type: 'select', 
                        options: [
                            { value: 'CallTool', label: 'Call Tool' },
                            { value: 'ListTools', label: 'List Tools' },
                            { value: 'ReadResource', label: 'Read Resource' },
                            { value: 'ListResources', label: 'List Resources' },
                            { value: 'GetPrompt', label: 'Get Prompt' },
                            { value: 'ListPrompts', label: 'List Prompts' }
                        ],
                        defaultValue: 'CallTool'
                    }}
                    value={data.mcpOperation}
                    onChange={onChange}
                />

                <PropertyField 
                    config={{ 
                        name: 'headers', 
                        label: 'Headers (JSON)', 
                        type: 'object', 
                        rows: 3,
                        placeholder: '{\n  "Authorization": "Bearer token"\n}'
                    }}
                    value={data.headers}
                    onChange={onChange}
                />

                {/* Operation Specific Fields */}
                <div className="pt-2 animate-in fade-in slide-in-from-top-1">
                    {operation === 'CallTool' && (
                        <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                <Wrench size={12} /> Tool Configuration
                            </div>
                            <PropertyField 
                                config={{ name: 'mcpToolName', label: 'Tool Name', type: 'text', placeholder: 'read_file', validation: { required: true } }}
                                value={data.mcpToolName}
                                onChange={onChange}
                            />
                            <PropertyField 
                                config={{ name: 'mcpToolArguments', label: 'Arguments (JSON)', type: 'object', rows: 4, placeholder: '{\n  "path": "/tmp/file.txt"\n}' }}
                                value={data.mcpToolArguments}
                                onChange={onChange}
                            />
                        </div>
                    )}

                    {operation === 'ReadResource' && (
                        <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                <FileText size={12} /> Resource Configuration
                            </div>
                            <PropertyField 
                                config={{ name: 'mcpResourceUri', label: 'Resource URI', type: 'text', placeholder: 'file:///logs/app.log', validation: { required: true } }}
                                value={data.mcpResourceUri}
                                onChange={onChange}
                            />
                        </div>
                    )}

                    {operation === 'GetPrompt' && (
                        <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                <Terminal size={12} /> Prompt Configuration
                            </div>
                            <PropertyField 
                                config={{ name: 'mcpPromptName', label: 'Prompt Name', type: 'text', placeholder: 'summarize', validation: { required: true } }}
                                value={data.mcpPromptName}
                                onChange={onChange}
                            />
                            <PropertyField 
                                config={{ name: 'mcpPromptArguments', label: 'Arguments (JSON)', type: 'object', rows: 4, placeholder: '{\n  "style": "concise"\n}' }}
                                value={data.mcpPromptArguments}
                                onChange={onChange}
                            />
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                    <PropertyField 
                        config={{ 
                            name: 'resultVariable', 
                            label: 'Result Variable', 
                            type: 'text', 
                            placeholder: 'mcpResult',
                            description: 'Stores the operation response.'
                        }}
                        value={data.resultVariable}
                        onChange={onChange}
                    />
                    
                    <PropertyField 
                        config={{ 
                            name: 'timeout', 
                            label: 'Timeout (s)', 
                            type: 'number', 
                            defaultValue: 30
                        }}
                        value={data.timeout}
                        onChange={onChange}
                    />
                </div>
            </div>
        </div>
    );
};